import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { Box, Flex, Text, Stack, HStack, Icon, SimpleGrid, Spinner, Link } from "@chakra-ui/react";
import { HardHat, Truck, Siren, ClipboardCheck, BookOpen, MapPin, CloudUpload } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { getOutbox, cachePut, cacheGet, safeAll } from "@/lib/offline";
import { Greeting, HomeCard, QuickLink, EmptyState, RecentSubs } from "@/pages/home/shared";
import { AssignedTasks } from "@/pages/Tasks";
import { SafetyBulletinsCard } from "@/components/SafetyBulletinsCard";
import { colors } from "@/theme/colors";

const TILES = [
  { type: "FLAH", label: "Hazard Assessment", sub: "FLAH", icon: HardHat, tint: colors.tealTint, fg: colors.teal },
  { type: "Vehicle Inspection", label: "Vehicle Inspection", sub: "Pre-trip check", icon: Truck, tint: "#EAF0E7", fg: colors.sageDeep },
  { type: "Incident Report", label: "Incident Report", sub: "Report it now", icon: Siren, tint: colors.poppyTint, fg: colors.poppy },
  { type: "Inspection", label: "Site Inspection", sub: "General inspection", icon: ClipboardCheck, tint: "#FBEEDA", fg: "#8A5106" },
];

export default function EmployeeHome() {
  const { user, profile, companyName, hasFeature, activeCompanyId } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [mySubs, setMySubs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [queued, setQueued] = useState(0);

  const load = useCallback(async () => {
    // Tenant isolation (WRITE PATH): pickers must only offer the ACTING
    // company's options — masters (company_id null) plus own-company rows.
    const [tplRes, subRes, locRes, slRes] = await safeAll([
      supabase.from("templates").select("*").eq("status", "published")
        .or(`company_id.is.null,company_id.eq.${activeCompanyId}`),
      supabase.from("submissions").select("id,template_title,template_type,location_name,worker_name,status,flagged,revision,created_at")
        .eq("submitted_by", user.id).eq("company_id", activeCompanyId).order("created_at", { ascending: false }).limit(5),
      supabase.from("locations").select("id,name,address").eq("active", true)
        .eq("company_id", activeCompanyId).order("name"),
      supabase.from("select_lists").select("*")
        .or(`company_id.is.null,company_id.eq.${activeCompanyId}`),
    ]);
    if (tplRes.error || locRes.error) {
      // Offline: serve the last-known-good lists so new forms stay startable.
      const snap = cacheGet(user.id, "home") || {};
      setTemplates(snap.templates || []);
      setMySubs(snap.mySubs || []);
      setLocations(snap.locations || []);
      setLoadFailed(true);
    } else {
      setTemplates(tplRes.data || []);
      setMySubs(subRes.data || []);
      setLocations(locRes.data || []);
      setLoadFailed(false);
      cachePut(user.id, "home", { templates: tplRes.data || [], mySubs: subRes.data || [], locations: locRes.data || [], lists: slRes.data || [] });
    }
    setQueued((await getOutbox(user.id)).length);
    setLoading(false);
  }, [user.id, activeCompanyId]);
  useEffect(() => { load(); }, [load]);

  // One matching template -> straight into the form; several -> pick on Field Forms.
  const startForm = (type) => {
    const matches = templates.filter((t) => t.type === type);
    if (matches.length === 1) navigate(`/forms/fill/${matches[0].id}`);
    else navigate("/forms");
  };

  if (loading) return <Flex minH="40vh" align="center" justify="center"><Spinner color={colors.teal} borderWidth="3px" /></Flex>;

  return (
    <Box data-testid="home-employee" maxW="640px">
      <Greeting name={profile?.full_name} sub={`Ready for a safe shift at ${companyName || "your company"}?`} />

      {queued > 0 && (
        <HStack mb="5" px="4" py="3" borderRadius="12px" bg="#FBEEDA" color="#8A5106" data-testid="home-outbox-banner">
          <Icon as={CloudUpload} boxSize="4" />
          <Text fontSize="sm" fontWeight="600">{queued} form{queued === 1 ? "" : "s"} waiting to sync — will send when you're online.</Text>
        </HStack>
      )}

      {/* Tasks assigned to this worker */}
      <Flex justify="space-between" align="center" mb="2.5">
        <Text fontWeight="700" fontSize="sm" color={colors.textPrimary}>Assigned to you</Text>
        <Link as={RouterLink} to="/tasks" fontSize="xs" color={colors.tealHover} fontWeight="600" data-testid="home-view-tasks">View all</Link>
      </Flex>
      <Box mb="6"><AssignedTasks compact /></Box>

      {/* Big phone-first quick-start tiles */}
      <Text fontWeight="700" fontSize="sm" color={colors.textPrimary} mb="2.5">Start a form</Text>
      <SimpleGrid columns={2} gap="3" mb="6">
        {TILES.map((t) => (
          <Flex key={t.type} as="button" type="button" onClick={() => startForm(t.type)}
            data-testid={`home-tile-${t.type.toLowerCase().replace(/\s+/g, "-")}`}
            direction="column" align="flex-start" textAlign="left" gap="2.5" bg={colors.white}
            border="1px solid" borderColor={colors.border} borderRadius="16px" p="4" minH="112px"
            _hover={{ borderColor: t.fg }} _active={{ transform: "scale(0.98)" }} transition="border-color 0.15s, transform 0.1s">
            <Flex align="center" justify="center" w="42px" h="42px" borderRadius="12px" bg={t.tint}>
              <Icon as={t.icon} boxSize="5" color={t.fg} />
            </Flex>
            <Box>
              <Text fontWeight="700" fontSize="sm" color={colors.textPrimary}>{t.label}</Text>
              <Text fontSize="xs" color={colors.textSecondary}>{t.sub}</Text>
            </Box>
          </Flex>
        ))}
      </SimpleGrid>

      <Stack gap="4">
        <SafetyBulletinsCard />

        <HomeCard title="My recent submissions" testId="home-my-subs"
          action={<Link as={RouterLink} to="/forms" fontSize="xs" color={colors.tealHover} fontWeight="600" data-testid="home-view-all-subs">View all</Link>}>
          {mySubs.length === 0 ? (
            <EmptyState>{loadFailed ? "You're offline — your recent submissions will appear once this device has loaded them." : "No submissions yet — start your first form above."}</EmptyState>
          ) : (
            <RecentSubs subs={mySubs} />
          )}
        </HomeCard>

        {hasFeature("safety_bulletins") && <QuickLink icon={BookOpen} label="Resources" desc="Manuals, procedures and safety bulletins" to="/resources" testId="home-link-resources" />}

        <HomeCard title="My locations" testId="home-my-locations">
          {locations.length === 0 ? (
            <EmptyState>{loadFailed ? "You're offline — your job sites will appear once this device has loaded them." : "No job sites assigned yet — ask your admin."}</EmptyState>
          ) : (
            <Stack gap="2">
              {locations.map((l) => (
                <HStack key={l.id} gap="2.5" px="3" py="2" borderRadius="10px" bg={colors.page}>
                  <Icon as={MapPin} boxSize="4" color={colors.teal} />
                  <Box>
                    <Text fontSize="sm" fontWeight="600" color={colors.textPrimary}>{l.name}</Text>
                    {l.address && <Text fontSize="xs" color={colors.textSecondary}>{l.address}</Text>}
                  </Box>
                </HStack>
              ))}
            </Stack>
          )}
        </HomeCard>
      </Stack>
    </Box>
  );
}
